import { Injectable } from '@angular/core';
import { type AbstractControl, type ValidationErrors } from '@angular/forms';
import { byteLength, Consts, I18nService, UNPLACEHOLDER, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { type OnDemandPolicy } from 'app/common/types/mo';

/**
 * 创建防病毒按需扫描任务
 */
export interface CreatingAnOnDemandAntivirusScanTaskReq {
    // 文件系统ID。
    fileSystemID?: number;
    // 防病毒按需扫描任务开关。
    switch: boolean;
    // 文件系统名。
    fsName?: string;
    // 租户ID。
    vstoreId?: string;
    // 租户Name。
    vstoreName?: string;
    // 立即执行任务扫描持续时长。
    immediatelyDuration?: string;
    // 当前是否为立即执行任务正在运行。
    isImmediatelyTask?: boolean;
    // 防病毒策略ID。
    policyId: string;
}

/**
 * 创建防病毒实时扫描任务
 */
export interface CreatingAnOnAccessAntivirusScanTaskReq {
    // 防病毒实时扫描任务开关。
    switch: boolean;
    // 租户ID。
    vstoreId?: string;
    // 文件系统ID。
    fileSystemID?: number;
    // 文件系统名。
    fsName?: string;
    // 租户名称。
    vstoreName?: string;
    // 防病毒策略ID。
    policyId: string;
}

/**
 * 内置实时扫描策略名称
 */
export const DefaultOnAccessPolicyName = 'default';

/**
 * 厂商枚举
 */
export enum VendorE {
    SYMANTEC_PROTECTION_ENGINE = 0,
    SYMANTEC_ENDPOINT_PROTECTION = 1,
    TREND_MICRO = 2,
    MC_AFEE = 3,
    SOPHOS = 4,
    KASPERSKY = 5,
    SHARE_OPEN = 6,
    META_DEFENDER = 7,
    RISINGI_CAP = 8,
    META_DEFENDER_ENHANCE = 9,
    TIANQING = 10,
}

/**
 * 服务器状态枚举
 */
export enum ServerStatusE {
    // 正常
    NORMAL = 1,
    // 失效
    INVALID = 35,
    // 过载
    OVERLOAD = 89,
    // 初始化中
    INITIALIZING = 53
}

/**
 * 扫描策略类型枚举
 */
export enum ScanStatusTypeE {
    READY = 0,
    SCANNING = 1,
    STOPED = 2
}

/**
 * 扫描策略类型枚举
 */
export enum ScanPolicyTypeE {
    ONACCESS = 'On-Access',
    ONDEMAND = 'On-Demand'
}

/**
 * 扫描策略类型枚举
 */
export enum ScanMissionEntryTypeE {
    CREATE = 'create',
    MODIFY = 'modify'
}

/**
 * 调度类型枚举
 */
export enum ScheduleTypeE {
    PERIOD = 0,
    WEEKLY = 1,
    MANUAL = 2
}

/**
 * 持续时间类型枚举
 */
export enum DurationTypeE {
    // 自定义
    CUSTOM = 'custom',
    // 直到完成
    UNTILFINISHED = 'until_scan_completes'
}

/**
 * 扫描时机枚举
 */
export enum ScanTriggerE {
    CLOSE = '0',
    OPEN_CLOSE = '1'
}

/**
 * 扫描任务状态枚举
 */
export enum TaskStatusE {
    DISABLE = '0',
    ENABLE = '1'
}

/**
 * 立即扫描状态枚举
 */
export enum ImmediatelyTaskE {
    DISABLE = '0',
    ENABLE = '1'
}

export enum ImmediatelyDurationBoundaryE {
    MIN = 30,
    MAX = 168,
    ZERO_VALUE = 0,
}

/**
 * 不扫描文件类型/路径枚举
 */
export enum ExcludeFileE {
    /**
     * 不扫描文件类型
     */
    TYPE = 'Type',
    /**
     * 不扫描文件路径
     */
    PATH = 'Path'
}

const MIN_SCAN_DURATION_TIME_MIN = '30';
const MAX_SCAN_DURATION_TIME_HOUR = '24';
const ON_DEMAND_MAX_SCAN_DURATION_TIME_HOUR = '168';

@Injectable({ providedIn: 'root' })
export class AntivirusStrService {
    constructor(
        private i18nService: I18nService,
        private utilService: UtilService,
    ) { }
    get = this.i18nService.get;
    isZh = this.i18nService.language === 'zh';
    /**
     * 默认持续时间-小时
     */
    defaultDurationHour = 8;
    /**
     * 默认持续时间-分钟
     */
    defaultDurationMinute = 0;
    /**
     * 路径分隔符
     */
    filePathSeperator = [':'];
    /**
     * 路径最大长度
     */
    filePathMaxLen = 1791;
    /**
     * 路径最大个数
     */
    filePathMaxNum = 100;
    /**
     * 类型最大长度
     */
    fileTypeMaxLen = 895;
    /**
     * 类型最大个数
     */
    fileTypeMaxNum = 200;

    i18n = {
        antivirusPolicyTitle: this.get('Antivirus Policy'),
        antivirusTitle: this.get('settings.Antivirus_task_title'),
        noAntivirusServerTip: this.get('settings.Antivirus_task_unconfiged_info'),
        addAntivirusServer: this.get('settings.Antivirus_task_manageServer_addServer_title'),
        existAntivirusServerTip: this.get('settings.Antivirus_task_configed_info', true),
        manageAntivirusServer: this.get('settings.Antivirus_task_manageServer_btn'),
        configScanUserBtn: this.get('settings.Antivirus_task_scanAuthUser_btn'),
        modifyPreSharedKey: this.get('settings.Antivirus_task_modifyPreSharedKey_btn'),
        configScanUserTip: this.get('settings.Antivirus_task_scanAuthUser_info'),
        modifyPreSharedKeyTip: this.get('settings.Antivirus_task_modifyPreSharedKey_info'),
        addAntivirusServerTitle: this.get('settings.Antivirus_task_manageServer_addServer_title'),
        addServerTips: this.get('settings.Antivirus_task_manageServer_serverInvalid_info'),
        removeAntivirusServerTitle: this.i18nService.get('settings.Antivirus_task_manageServer_removeServer_title'),
        removeAntivirusServerTip: this.i18nService.get('settings.Antivirus_task_manageServer_removeServer_msg'),
        basicInfo: this.get('common.basicInfo_title'),
        name: this.get('common.name_column'),
        id: this.get('common.ID_column'),
        fsName: this.get('service.quota_term_FSName_lab'),
        fsId: this.get('settings.Antivirus_term_detail_fsID_lab'),
        capacity: this.get('common.capacity_column'),
        vendor: this.get('settings.Antivirus_task_manageServer_vendor_lab'),
        vendorTip: this.get('settings.Antivirus_task_manageServer_addServer_sameVendor_info'),
        ipAddress: this.get('common.IPaddress_column'),
        port: this.get('common.port'),
        ipAndPort: `${this.get('common.IPaddress_column')}:${this.get('common.port')}`,
        portTip: this.get('common.valueRange_label', [1, 65535]),
        presharedKey: this.get('settings.Antivirus_task_modifyPreSharedKey_newPwd_lab'),
        confirmPresharedKey: this.get('settings.Antivirus_task_modifyPreSharedKey_confirmPwd_lab'),
        serviceDisabled: this.get('settings.Antivirus_task_disable_info'),
        serviceEnabled: this.get('settings.Antivirus_task_enable_info'),
        noAvailableServer: this.get('settings.Antivirus_task_failed_info'),
        ipPlaceholder: this.get('common.IPv4orIPv6_label'),
        ipErrorTip: this.get('common.IPAddr_validation'),
        downloadPluginTitle: this.get('settings.Antivirus_task_manageServer_downAVAgentLoad_title'),
        downloadPluginTip: this.get('settings.Antivirus_task_manageServer_downloadSoftware_info'),
        downloadPluginHighRisk: this.get('settings.Antivirus_task_manageServer_downAVAgent_confim_msg'),
        status: this.get('common.status_column'),
        statusLab: this.get('common.status_label'),
        immediateStatus: this.get('settings.Antivirus_task_oneDemandScanPolicy_status_scanningNow_value'),
        scanedLab: this.get('settings.Antivirus_term_detail_scaned_lab', true),
        scanStartTime: this.get('settings.Antivirus_term_detail_lastTimeScanStartTime_lab'),
        scanEndTime: this.get('settings.Antivirus_term_detail_lastTimeScanEndTime_lab'),
        scannedObjs: this.get('settings.Antivirus_term_detail_scanObjects_lab'),
        scanResultLab: this.get('settings.Antivirus_term_detail_scanResult_lab'),
        scanedResultLab: this.get('settings.Antivirus_term_detail_lastTimeScanResult_lab'),
        scanResultValue: (value: string[]): string => this.get('settings.Antivirus_term_detail_lastTimeScanResult_value', value),
        onaccessScanResultValue: (value: string[]): string => this.get('settings.Antivirus_term_detail_scanResult_value', value),
        scanFailedLab: this.get('settings.Antivirus_term_detail_failed_lab'),
        scanFailedLabWithColon: this.get('settings.Antivirus_term_detail_failed_lab', true),
        scanVirusLab: this.get('settings.Antivirus_term_detail_scanVirusFile_lab'),
        scanedVirusLab: this.get('settings.Antivirus_term_detail_scanedVirusFile_lab'),
        lastScanVirusLab: this.get('settings.Antivirus_term_detail_lastTimeScanVirusFile_lab'),
        scanProgress: this.get('system.upgrade_term_progress_lab'),
        fsScanMission: this.get('settings.Antivirus_task_fileSystemScanTask_tab'),
        onAccessScan: this.get('settings.Antivirus_task_fileSystemScanTask_oneAccessScanPolicy_col'),
        onAccessScanTab: this.get('settings.Antivirus_task_oneAccessScanPolicy_tab'),
        onDemandScan: this.get('settings.Antivirus_task_createFSScanTask_oneDemandScanPolicy_checkbox'),
        onDemandScanTab: this.get('settings.Antivirus_task_oneDemandScanPolicy_tab'),
        onDemandStart: this.get('settings.Antivirus_task_oneDemandScanPolicy_enable_btn'),
        onDemandStop: this.get('settings.Antivirus_task_oneDemandScanPolicy_disable_btn'),
        onAccessStart: this.get('settings.Antivirus_task_oneAccessScanPolicy_enable_btn'),
        onAccessStop: this.get('settings.Antivirus_task_oneAccessScanPolicy_disable_btn'),
        createScanMissionTitle: this.get('settings.Antivirus_task_createFSScanTask_title'),
        modifyScanMissionTitle: this.get('settings.Antivirus_task_modifyFSScanTask_title'),
        avaiableFsLab: this.get('protection.HyperCDPSchedule_task_create_step1_availFS_lab'),
        selectedLab: this.get('protection.HyperCDPSchedule_task_create_step1_selectedFS_lab'),
        selectedEmptyErr: this.get('common.selectOneObject_lab'),
        policyEmptyErr: this.get('settings.Antivirus_task_createFSScanTask_selectOne_info'),
        configurePolicyTip: this.get('settings.Antivirus_task_createFSScanTask_info'),
        policyLab: this.get('settings.Antivirus_task_createFSScanTask_scanPolicy_lab'),
        onaccessPolicyLab: this.get('settings.Antivirus_task_createFSScanTask_scanPolicy_lab'),
        scanPlan: this.get('settings.Antivirus_task_createFSScanTask_scanPlan_lab'),
        unscannedFileType: this.get('settings.Antivirus_task_createFSScanTask_excludeFileType_lab'),
        unscannedFileTypeTip: this.get('settings.Antivirus_task_createFSScanTask_excludeFileType_tip', [1, 8]),
        excludePeriod: this.get('settings.Antivirus_task_createFSScanTask_noScanPeriod_lab'),
        excludeFilePath: this.get('settings.Antivirus_task_createFSScanTask_excludeFilePath_lab'),
        excludeFilePathTip: this.get('settings.Antivirus_task_createFSScanTask_excludeFilePath_tip', [1, this.filePathMaxLen]),
        timeout: this.get('settings.Antivirus_task_createFSScanTask_timeout_lab'),
        accessAfterTimeout: this.get('settings.Antivirus_task_createFSScanTask_timeoutAccess_lab'),
        scanTrigger: this.get('settings.Antivirus_task_createFSScanTask_triggerPolicy_lab'),
        maxScanFileSize: this.get('settings.Antivirus_task_createFSScanTask_excludeFileSize_lab'),
        onDemandPolicyLab: this.get('settings.Antivirus_task_view_oneDemandScanPolicy_lab'),
        onDemandPolicyCol: this.get('settings.Antivirus_task_fileSystemScanTask_oneDemandScanPolicy_col'),
        onDemandPolicyId: this.get('settings.Antivirus_term_detail_onDemandPolicyID_lab'),
        onDemandPolicyStatus: this.get('settings.Antivirus_task_fileSystemScanTask_oneDemandScanPolicyStatus_col'),
        onAccessPolicyCol: this.get('settings.Antivirus_task_fileSystemScanTask_oneAccessScanPolicyName_col'),
        onAccessPolicyId: this.get('settings.Antivirus_task_fileSystemScanTask_oneAccessScanPolicyID_col'),
        onAccessPolicyStatus: this.get('settings.Antivirus_task_fileSystemScanTask_oneAccessScanPolicyStatus_col'),
        onDemandScanStatus: this.get('settings.Antivirus_task_fileSystemScanTask_oneDemandScanStatus_col'),
        createOnDemandPolicy: this.get('settings.Antivirus_task_oneDemandScanPolicy_create_title'),
        modifyOnDemandPolicy: this.get('settings.Antivirus_task_oneDemandScanPolicy_modify_title'),
        deleteOndemandPolicy: this.get('settings.Antivirus_task_oneDemandScanPolicy_delete_title'),
        deleteOndemandPolicyTip: this.get('settings.Antivirus_task_oneDemandScanPolicy_delete_info_msg'),
        deleteOndemandTask: this.get('settings.Antivirus_task_deleteFSScanTask_title'),
        deleteOndemandTaskTip: this.get('settings.Antivirus_task_deleteFSScanTask_info_msg'),
        policyCardOnDemandTip: this.get('settings.Antivirus_task_createFSScanTask_oneDemandScan_info'),
        policyCardOnAccessTip: this.get('settings.Antivirus_task_createFSScanTask_oneAccessScan_info'),
        scheduleType: this.get('settings.Antivirus_task_oneDemandScanPolicy_timePolicy_lab'),
        createOnAccessPolicy: this.get('settings.Antivirus_task_oneAccessScanPolicy_create_title'),
        createOnAccessPolicyHighRiskTip: this.get('settings.filesystem_term_createAntivirusScanPolicy_msg'),
        modifyOnAccessPolicy: this.get('settings.Antivirus_task_oneAccessScanPolicy_modify_title'),
        modifyOnAccessPolicyHighRiskTip: this.get('settings.filesystem_term_changeAntivirusScanPolicy_msg'),
        deleteOnaccessPolicy: this.get('settings.Antivirus_task_oneAccessScanPolicy_delete_title'),
        deleteOnaccessPolicyTip: this.get('settings.Antivirus_task_oneAccessScanPolicy_delete_info_msg'),
        day: this.get('common.lower_day1_label'),
        week: this.get('common.week_label'),
        startTime: this.get('common.startTime_label'),
        duration: this.get('common.duration_label'),
        hour: this.get('common.timeAcount_hours_label'),
        minute: this.get('common.timeAcount_minutes_label'),
        second: this.get('common.time_seconds_label'),
        restricted: this.get('settings.Antivirus_task_oneAccessScanPolicy_restricted_checkbox'),
        unRestricted: this.get('settings.Antivirus_task_oneDemandScanPolicy_view_notRestricted_value'),
        executePeriod: this.get('settings.Antivirus_task_oneDemandScanPolicy_timePolicy_PeriodPolicy_lab'),
        custom: this.get('common.custom_label'),
        untilFinished: this.get('settings.Antivirus_task_oneDemandScanPolicy_timePolicy_untilScanFinish_value'),
        everyPrep: this.get('common.everyPrep_label'),
        performSuffix: this.get('settings.Antivirus_task_oneDemandScanPolicy_timePolicy_execute_lab'),
        enable: this.get('common.enable_label'),
        disable: this.get('common.disable0_label'),
        viewAll: this.get('common.clickToViewAll_info'),
        yesLabel: this.get('common.term_yes_label'),
        noLabel: this.get('common.term_No_label'),
        immediately: this.get('settings.Antivirus_task_scanNow_btn'),
        startImmediatelyTip: this.get('settings.Antivirus_task_scanNow_info'),
        moreLabel: this.get('common.more_button'),
        foldLabel: this.get('settings.upgrade_task_fold_btn'),
        defaultCannotOperated: this.get('settings.Antivirus_task_oneAccessScanPolicy_deleteDefault_err_tip'),
        onAccessScanTaskDisabledTip: this.get('settings.Antivirus_task_FSScanTask_onAccessPolicy_err_tip'),
        onDemandScanTaskDisabledTip: this.get('settings.Antivirus_task_FSScanTask_onDemandPolicy_err_tip'),
        taskEnableErrTip: this.get('settings.Antivirus_task_scanPolicy_enable_err_tip'),
        taskDisableErrTip: this.get('settings.Antivirus_task_scanPolicy_disable_err_tip'),
        specifyTimeErr: this.get('protection.HyperCDPSchedule_task_create_step2_weeklyExecute_tip'),
        fileTypeNumErr: this.get('settings.Antivirus_task_createFSScanTask_excludeFileTypeNum_valid'),
        fileTypeLengthErr: (): string => this.get('common.totalLenth_valid', [this.fileTypeMaxLen]),
        fileTypeNameErr: this.get('settings.Antivirus_task_createFSScanTask_excludeFileType_valid'),
        filePathNameErr: this.get('settings.Antivirus_task_createFSScanTask_excludeFilePath_valid'),
        filePathNumErr: this.get('settings.Antivirus_task_createFSScanTask_excludeFilePathNum_valid'),
        filePathLengthErr: this.get('settings.Antivirus_task_createFSScanTask_totalLenth_valid'),
        excludePeriodSameErr: this.get('settings.Antivirus_task_createFSScanTask_noScanPeriod_notSame_valid'),
        excludePeriodIntersectErr: this.get('settings.Antivirus_task_createFSScanTask_noScanPeriod_intersect_valid'),
        excludePeriodNumErr: this.get('settings.Antivirus_task_createFSScanTask_noScanPeriodNum_valid'),
        excludePeriodDateCompareErr: this.get('storage.smartMigration_task_create_dateCompareErr_tip'),
        emptyExcludePeriodErr: (formItem: string): string => this.get('common.null_validation', [formItem]),

        okBtn: this.get('common.confirm_button'),
        cancelBtn: this.get('common.cancel_button'),
        closeBtn: this.get('common.close_button'),
        addBtn: this.get('common.add_button'),
        deleteBtn: this.get('common.delete_button'),
        removeBtn: this.get('common.remove_button'),
        operationBtn: this.get('common.operation_button'),
        testBtn: this.get('common.test'),
        downloadBtn: this.get('settings.Antivirus_task_manageServer_downLoad_btn'),
        createBtn: this.get('common.create_button'),
        modifyBtn: this.get('common.modify_button'),
        period: this.get('common.Punctuation_preiod'),

        capacityRangeErr: (min: string, max: string): string => this.get('service.fileSystem_term_capaRange_tip', [min, max]),
        addAntivirusServerAction: (value: string): string => this.get('rest.addAntivirusServer_msg', [value]),
        removeAntivirusServerAction: (value: string): string => this.get('rest.removeAntivirusServer_msg', [value]),
        createOnDemandPolicyAction: (value: string): string => this.get('rest.createOneDemandScanPolicy_msg', [value]),
        modifyOnDemandPolicyAction: (value: string): string => this.get('rest.modifyOneDemandScanPolicy_msg', [value]),
        deleteOnDemandPolicyAction: (value: string): string => this.get('rest.deleteOneDemandScanPolicy_msg', [value]),
        createOnAccessPolicyAction: (value: string): string => this.get('rest.createOneAccessScanPolicy_msg', [value]),
        modifyOnAccessPolicyAction: (value: string): string => this.get('rest.modifyOneAccessScanPolicy_msg', [value]),
        deleteOnAccessPolicyAction: (value: string): string => this.get('rest.deleteOneAccessScanPolicy_msg', [value]),
        createOnDemandTaskAction: (value: string): string => this.get('rest.createFSScanTask_msg', [value]),
        modifyOnDemandTaskAction: (value: string): string => this.get('rest.modifyFSScanTask_msg', [value]),
        deleteOnDemandTaskAction: (value: string): string => this.get('rest.deleteFSScanTask_msg', [value]),
        createOnAccessTaskAction: (value: string): string => this.get('rest.createFSOnAccessScanTask_msg', [value]),
        modifyOnAccessTaskAction: (value: string): string => this.get('rest.modifyFSOnAccessScanTask_msg', [value]),
        deleteOnAccessTaskAction: (value: string): string => this.get('rest.deleteFSOnAccessScanTask_msg', [value]),
        startOnDemandTaskAction:
            (fsName: string, policyName: string): string => this.get('rest.enableOneDemandScanPolicy_msg', [fsName, policyName]),
        stopOnDemandTaskAction:
            (fsName: string, policyName: string): string => this.get('rest.disableOneDemandScanPolicy_msg', [fsName, policyName]),
        startOnAccessTaskAction:
            (fsName: string, policyName: string): string => this.get('rest.enableOneAccessScanPolicy_msg', [fsName, policyName]),
        stopOnAccessTaskAction:
            (fsName: string, policyName: string): string => this.get('rest.disableOneAccessScanPolicy_msg', [fsName, policyName]),
        startNowAction: (fsName: string): string => this.get('rest.startScanNow_msg', [fsName]),
        modifyPreSharedKeyAction: this.get('rest.modifyPreSharedKey_msg'),
        enableServiceAction: this.get('rest.enableAntivirusService_msg'),
        disableServiceAction: this.get('rest.disableAntivirusService_msg'),
        scanDurationRangeTip:
            this.get(
                'common.range_validation',
                [
                    this.get('common.duration_label'),
                    MIN_SCAN_DURATION_TIME_MIN + (this.isZh ? '' : ' ') + this.get('common.timeAcount_minutes_label'),
                    MAX_SCAN_DURATION_TIME_HOUR + (this.isZh ? '' : ' ') + this.get('common.timeAcount_hours_label'),
                ],
            ),
        onDemandScanDurationRangeTip:
            this.get(
                'common.range_validation',
                [
                    this.get('common.duration_label'),
                    MIN_SCAN_DURATION_TIME_MIN + (this.isZh ? '' : ' ') + this.get('common.timeAcount_minutes_label'),
                    ON_DEMAND_MAX_SCAN_DURATION_TIME_HOUR + (this.isZh ? '' : ' ') + this.get('common.timeAcount_hours_label'),
                ],
            ),
    };

    /**
     * 防病毒服务图标
     */
    icon = {
        serverAvailable: '#icon-status-succeed',
        serverUnavailable: '#app-icon-warning',
        serviceEnable: '#app-icon-antivirus-service-enable',
        serviceDisable: '#app-icon-antivirus-service-disable',
        serviceError: '#app-icon-antivirus-service-error',
        taskEnable: '#app-icon-antivirus-task-enable',
        taskDisable: '#app-icon-antivirus-task-disable',
    };

    /**
     * 产商选项
     */
    vendorOptions: SelectOption[] = [
        {
            label: this.get('settings.EnumDefine_AV_SERVER_VENDOR_E_SYMANTEC'),
            value: VendorE.SYMANTEC_PROTECTION_ENGINE,
        },
        {
            label: this.get('settings.EnumDefine_AV_SERVER_VENDOR_E_SYMANTE_ENDPOINT'),
            value: VendorE.SYMANTEC_ENDPOINT_PROTECTION,
        },
        {
            label: this.get('settings.EnumDefine_AV_SERVER_VENDOR_E_TREND'),
            value: VendorE.TREND_MICRO,
        },
        {
            label: this.get('settings.EnumDefine_AV_SERVER_VENDOR_E_MCAFEE'),
            value: VendorE.MC_AFEE,
        },
        {
            label: this.get('settings.EnumDefine_AV_SERVER_VENDOR_E_SOPHOS'),
            value: VendorE.SOPHOS,
        },
        {
            label: this.get('settings.EnumDefine_AV_SERVER_VENDOR_E_KASPERSKY'),
            value: VendorE.KASPERSKY,
        },
        {
            label: this.get('settings.EnumDefine_AV_SERVER_VENDOR_E_ShareOpen'),
            value: VendorE.SHARE_OPEN,
        },
        {
            label: this.get('settings.EnumDefine_AV_SERVER_VENDOR_E_OPSWAT'),
            value: VendorE.META_DEFENDER,
        },
        {
            label: this.get('settings.EnumDefine_AV_SERVER_VENDOR_E_RISING_ICAP'),
            value: VendorE.RISINGI_CAP,
        },
        {
            label: this.get('settings.EnumDefine_AV_SERVER_VENDOR_E_OPSWATEnhance'),
            value: VendorE.META_DEFENDER_ENHANCE,
        },
        {
            label: this.get('settings.EnumDefine_AV_SERVER_VENDOR_E_Tianqing'),
            value: VendorE.TIANQING,
        },
    ];

    /**
     * 调度类型选项
     */
    scheduleTypeList: SelectOption[] = [
        {
            label: this.get('settings.Antivirus_task_oneDemandScanPolicy_timePolicy_weekly_value'),
            value: ScheduleTypeE.WEEKLY,
        },
        {
            label: this.get('settings.Antivirus_task_oneDemandScanPolicy_timePolicy_fixedPeriod_value'),
            value: ScheduleTypeE.PERIOD,
        },
        {
            label: this.get('common.EnumDefine_STARTUP_MODE_E_MANUAL'),
            value: ScheduleTypeE.MANUAL,
        },
    ];

    /**
     * 持续时间类型列表
     */
    durationTypeList: SelectOption[] = [
        {
            label: this.i18n.custom,
            value: DurationTypeE.CUSTOM,
        },
        {
            label: this.i18n.untilFinished,
            value: DurationTypeE.UNTILFINISHED,
        },
    ];

    /**
     * 星期列表
     */
    weekList: SelectOption[] = [
        {
            label: this.get('common.seven_checkbox'),
            value: 7,
        },
        {
            label: this.get('common.one_checkbox'),
            value: 1,
        },
        {
            label: this.get('common.two_checkbox'),
            value: 2,
        },
        {
            label: this.get('common.three_checkbox'),
            value: 3,
        },
        {
            label: this.get('common.four_checkbox'),
            value: 4,
        },
        {
            label: this.get('common.five_checkbox'),
            value: 5,
        },
        {
            label: this.get('common.six_checkbox'),
            value: 6,
        },
    ];

    scanTriggerList: SelectOption[] = [
        {
            label: this.get('settings.Antivirus_task_createFSScanTask_triggerPolicy_close_lab'),
            value: ScanTriggerE.CLOSE,
        },
        {
            label: this.get('settings.Antivirus_task_createFSScanTask_triggerPolicy_openClose_lab'),
            value: ScanTriggerE.OPEN_CLOSE,
        },
    ];

    /**
     * 最大可扫描文件大小容量单位
     */
    maxScanCapacityUnit: string[] = [
        Consts.MB,
        Consts.GB,
    ];

    /**
     * 服务器状态
     */
    statusOpt = this.getI18n({
        // 正常
        1: 'common.EnumDefine_RUNNING_STATUS_E_NORMAL',
        // 失效
        35: 'common.EnumDefine_RUNNING_STATUS_E_INVALID',
        // 过载
        89: 'common.EnumDefine_RUNNING_STATUS_E_OVERLOAD',
        // 初始化
        53: 'common.EnumDefine_RUNNING_STATUS_INITIALIZING',
    });

    /**
     * 扫描状态
     */
    scanStatusOpt = this.getI18n({
        // 准备
        0: 'common.EnumDefine_RUNNING_STATUS_E_READY',
        // 扫描
        1: 'common.EnumDefine_RUNNING_STATUS_E_SCANNING',
        // 停止
        2: 'common.EnumDefine_RUNNING_STATUS_E_STOP',
        // 不支持只读
        3: 'common.EnumDefine_RUNNING_STATUS_E_NOSUPPORTREADONLYFS',
        // 排队中
        4: 'common.EnumDefine_SPLIT_STATUS_E_QUEUING',
    });

    /**
     * 任务状态
     */
    taskStatusOpt = this.getI18n({
        0: 'common.service_disable_value',
        1: 'common.service_enable_value',
    });

    /**
     * 获取不扫描文件路径下发参数
     */
    getExcludeFilePathParam(excludeFilePath: string[]): string {
        return excludeFilePath.map((item): string => item.replace(/,/g, ':')).join(',');
    }

    /**
     * 获取不扫描文件路径vm的值
     */
    getExcludeFilePathVmValue(excludeFilePath: string): string[] {
        return excludeFilePath.split(',').map((item): string => item.replace(/:/g, ','));
    }

    /**
     * 厂商render
     */
    vendorRender = (vendor: string | number): string => this.vendorOptions.find((option): boolean => option.value === Number(vendor)).label;

    /**
     * 最大文件可扫描大小render
     */
    maxFileSizeRender = (maxFileSize: string | number): string => {
        if (maxFileSize === '' || maxFileSize === undefined) {
            return UNPLACEHOLDER;
        }

        const capacityObj = this.utilService.adaptUnit(maxFileSize);
        if (capacityObj.value) {
            return capacityObj.label;
        } else {
            return this.i18n.unRestricted;
        }
    };

    /**
     * 不扫描文件类型render
     */
    excludeFileTypeRender = (fileTypes: string): string => {
        if (!fileTypes) {
            return UNPLACEHOLDER;
        }

        return fileTypes.replace(/,/g, ';');
    };

    /**
     * 不扫描文件路径气泡数据render
     */
    excludeFilePathMesRender = (filePath: string): string[] => filePath.split(',').map((item): string => item.replace(/:/g, ','));

    /**
     * 调度类型render
     */
    scheduleTypeRender = (scheduleType: string): string => this.scheduleTypeList.find((item): boolean => Number(scheduleType) === item.value).label;

    /**
     * 扫描状态render
     */
    scanStatusRender = (scanStatus: string | number): string => this.scanStatusOpt[scanStatus] || UNPLACEHOLDER;

    /**
     * 任务开关render
     */
    taskStatusRender = (taskStatus: string | number): string => this.taskStatusOpt[taskStatus] || UNPLACEHOLDER;

    /**
     * 立即扫描持续时间render
     */
    immediateDurationRender = (duration: string): string => {
        if (!duration || duration === UNPLACEHOLDER) {
            return UNPLACEHOLDER;
        } else if (duration === DurationTypeE.UNTILFINISHED) {
            return this.i18n.untilFinished;
        } else {
            const durationArray = duration.split(':').map((item): number => Number(item));
            return this.get('settings.Antivirus_task_oneDemandScanPolicy_duration_value', durationArray);
        }
    };

    /**
     * 按需扫描详情中策略状态render
     */
    onDemandDetailTaskStatusRender = (taskStatus: string, isImmediatelyTask: string): string => {
        if (isImmediatelyTask === ImmediatelyTaskE.ENABLE) {
            return this.i18n.immediateStatus;
        } else {
            return this.taskStatusRender(taskStatus);
        }
    };

    /**
     * 扫描计划render
     */
    scanPlanRender = (policyData: OnDemandPolicy): string => {
        let scanPlan = UNPLACEHOLDER;

        if (Number(policyData.scheduleType) === ScheduleTypeE.WEEKLY) {
            const week = policyData.dayOfWeek.split(',')
                .map((item): string => this.weekList.find((day): boolean => day.value === Number(item)).label).join(', ');
            const hour = policyData.duration.split(':')[0];
            const minute = policyData.duration.split(':')[1];
            scanPlan = this.get('settings.Antivirus_term_detail_plan_status1', [week, policyData.startTime, Number(hour), Number(minute)]);
        } else if (Number(policyData.scheduleType) === ScheduleTypeE.PERIOD) {
            const hour = policyData.duration.split(':')[0];
            const minute = policyData.duration.split(':')[1];
            scanPlan = policyData.duration === DurationTypeE.UNTILFINISHED ? 
                this.get('settings.Antivirus_term_detail_plan_status3', [policyData.period, policyData.startTime]) :
                this.get('settings.Antivirus_term_detail_plan_status2', [policyData.period, policyData.startTime, Number(hour), Number(minute)]);
        }

        return scanPlan;
    };

    /**
     * 超时是否可以访问render
     */
    accessAfterTimeoutRender = (accessAfterTimeout: string | number): string =>
        Number(accessAfterTimeout) === 0 ? this.i18n.noLabel : this.i18n.yesLabel;

    /**
     * 修正持续时间，ex: 8 -> '08'，便于后续拼接下发
     */
    fixDurationValue(val: number): string {
        return String(val).padStart(2, '0');
    }

    /**
     * 拼接持续时间
     */
    getDuration = (hour: number, minute: number): string => `${this.fixDurationValue(hour)}:${this.fixDurationValue(minute)}`;

    /**
     * 类型校验器
     */
    fileTypeValidator = (control: AbstractControl): ValidationErrors | null => {
        const reg = /^[0-9a-zA-Z*?_]*$/;
        const isValid = reg.test(control.value);

        return isValid ? null : { fileTypeName: true };
    };

    /**
     * 类型最大长度校验器
     */
    fileTypeLengthValidator = (control: AbstractControl): ValidationErrors | null => {
        const isValid = byteLength(control.value.join(',')) <= this.fileTypeMaxLen;

        return isValid ? null : { fileTypeLength: true };
    };

    /**
     * 类型最大个数校验器
     */
    fileTypeNumValidator = (control: AbstractControl): ValidationErrors | null => {
        const isValid = control.value.length <= this.fileTypeMaxNum;

        return isValid ? null : { fileTypeNum: true };
    };

    /**
     * 路径校验器
     */
    filePathValidator = (control: AbstractControl): ValidationErrors | null => {
        const reg = /^\/[^":|<>*?\\]{0,4094}$/;
        const isValid = reg.test(control.value);

        return isValid ? null : { filePathName: true };
    };

    /**
     * 路径最大长度校验器
     */
    filePathLengthValidator = (control: AbstractControl): ValidationErrors | null => {
        const isValid = byteLength(control.value.join(',')) <= this.filePathMaxLen;

        return isValid ? null : { filePathLength: true };
    };

    /**
     * 路径最大个数校验器
     */
    filePathNumValidator = (control: AbstractControl): ValidationErrors | null => {
        const isValid = control.value.length <= this.filePathMaxNum;

        return isValid ? null : { filePathNum: true };
    };

    getI18n(kv: { [key: string]: string }): { [key: string]: string } {
        const keys = Object.keys(kv);
        const result = {};
        keys.forEach((key): void => {
            result[key] = this.get(kv[key]);
        });
        return result;
    }

    getRangeTip(min: string, max: string): string {
        return this.i18nService.get('common.valueRange_label', [min, max]);
    }

    /**
     * 将字符串转为数字
     * @param data 不扫描文件类型/文件路径
     */
    convertToCount(data: string): number {
        if (data === UNPLACEHOLDER || !data) {
            return 0;
        }
        return data.split(',').length;
    }
}
